import { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import PlacesAutocomplete from "react-places-autocomplete";
import "./AddressAutocomplete.scss";
import waitForGoogleApi from "app/utils/waitForGoogleApi";
import InputFormik from "app/pages/.shared/form/InputFormik";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import { Field } from "formik";
import useGeolocation from "app/pages/.shared/useGeolocation";
import AppGlobalsContext from "app/AppGlobalsContext";
import { inputSanitize } from "app/utils/sanitize";

const AddressAutocompleteFormik = ({
	id,
	label,
	name,
	isRequired = false,
	onError,
	enableSuggestionWithPostCode,
	onPlaceSelect,
	onBeforePlaceSelect,
	setFieldValue,
	setFieldError,
	blocPrefixName = "",
	...restProps
}) => {
	const [enableGeolocation, toggleGeolocation] = useState(false);
	const [hasChanged, setHasChanged] = useState(false);

	const { latitude, longitude } = useGeolocation({ enable: enableGeolocation });

	const isGoogleApiReady = waitForGoogleApi();

	const { shop = "" } = useContext(AppGlobalsContext);

	const serializeAddressComponents = place => {
		const components = place.address_components;
		let data = {
			components: {},
			formattedAddress: "",
			name: "",
		};

		const streetNumber = components.find(component => component.types[0] === "street_number");
		const route = components.find(component => component.types[0] === "route");
		const city = components.find(
			component => component.types[0] === "locality" || component.types[0] === "postal_town"
		);
		const country = components.find(component => component.types[0] === "country");
		const postalCode = components.find(
			component => component.types[0] && component.types[0].includes("postal_code")
		);

		data.components.streetNumber = streetNumber && streetNumber.long_name;
		data.components.route = route && route.long_name;
		data.components.city = city && city.long_name;
		data.components.country = country && country.long_name;
		data.components.postalCode = postalCode && postalCode.long_name;
		data.formattedAddress = place.formatted_address;

		if (data.components.route) {
			data.name = data.components.route;

			if (data.components.streetNumber) {
				data.name = `${data.components.streetNumber} ${data.components.route}`;
			}
		} else {
			data.name = place.name;
		}

		return data;
	};

	const onChange = address => {
		const addressLine1Name = blocPrefixName
			? `${blocPrefixName.toLowerCase()}.address-line1`
			: "address-line1";
		const addressLine2Name = blocPrefixName
			? `${blocPrefixName.toLowerCase()}.address-line2`
			: "address-line2";
		const codePostalName = blocPrefixName
			? `${blocPrefixName.toLowerCase()}.postal-code`
			: "postal-code";
		const sanitizedAddress = inputSanitize(address);
		if (enableSuggestionWithPostCode) {
			setFieldValue(codePostalName, sanitizedAddress);
			if (address !== "" && !hasChanged) {
				setFieldError(codePostalName);
				setHasChanged(true);
			}
		} else {
			setFieldValue(addressLine1Name, sanitizedAddress);
			setFieldValue(addressLine2Name, "");
			if (address !== "" && !hasChanged) {
				setFieldError(addressLine1Name);
				setFieldError(addressLine2Name);
				setHasChanged(true);
			}
		}
	};

	const handleFocus = useCallback(() => {
		if (!enableGeolocation) {
			toggleGeolocation(true);
		}
	}, [enableGeolocation]);

	const onSelect = (address, placeId) => {
		// on valorise le champ avec le numero de rue et le nom de la rue avant l'arrivée du détail de l'adresse
		const rue = address.split(",")[0];
		onChange(rue);

		if (typeof onBeforePlaceSelect === "function") {
			onBeforePlaceSelect();
		}
		// eslint-disable-next-line no-undef
		const service = new google.maps.places.PlacesService(document.createElement("div"));
		service.getDetails({ placeId }, (place, status) => {
			// eslint-disable-next-line no-undef
			if (status === google.maps.places.PlacesServiceStatus.OK) {
				const placeData = serializeAddressComponents(place);
				if (typeof onPlaceSelect === "function") {
					onPlaceSelect(placeData);
				} else {
					onChange(placeData.formattedAddress);
				}
			} else if (typeof onError === "function") {
				onError("getDetails", status);
			}
		});
	};

	const getInputClassname = (field, meta, isGoogleApiReady) => {
		return classNames("address-autocomplete control-group", {
			"control-group--touched": meta.touched || field.value,
			"control-group--error": meta.touched && meta.error,
			"control-group--disabled": !isGoogleApiReady,
		});
	};
	const handleBlur = () => {
		setHasChanged(false);
	};
	// pour GB, [] pour permettre les suggestions basés sur un code postale
	let options = {
		types: enableSuggestionWithPostCode ? [] : ["address"],
	};

	const [, country] = shop.split("-");

	if (country) {
		options.componentRestrictions = {
			country,
		};
	}

	if (isGoogleApiReady && latitude && longitude) {
		// eslint-disable-next-line no-undef
		options.location = new google.maps.LatLng(latitude, longitude);
		options.radius = 1500000;
		options.strictbounds = true;
	}

	// should use `useField()` instead of <Field /> when migrated to functionalComponent
	return (
		<Field name={name}>
			{({ field, meta }) => {
				const shouldFetchSuggestions = Boolean(
					enableSuggestionWithPostCode
						? field.value && field.value.length > 3
						: field.value && field.value.length > 6
				);

				return (
					<div className={getInputClassname(field, meta, isGoogleApiReady)}>
						{isGoogleApiReady ? (
							<PlacesAutocomplete
								value={field.value}
								onChange={onChange}
								onSelect={onSelect}
								searchOptions={options}
								shouldFetchSuggestions={shouldFetchSuggestions}
								highlightFirstSuggestion={true}
							>
								{({
									getInputProps,
									suggestions = [],
									getSuggestionItemProps,
									loading,
								}) => {
									return (
										<div style={{ position: "relative" }}>
											<InputFormik
												id={id}
												name={name}
												isRequired={isRequired}
												label={label}
												{...getInputProps()}
												shouldSanitize={false}
												autoComplete="disabled"
												onFocus={handleFocus}
												data-testid={restProps["data-testid"]}
												onBlur={handleBlur}
											/>

											{loading && (
												<div className="address-autocomplete__loader">
													<HorizontalDotLoader
														dotSize="3px"
														color="#3493dc"
													/>
												</div>
											)}

											{suggestions.length > 0 && (
												<div className="address-autocomplete__container">
													{suggestions.map(suggestion => {
														return (
															<div
																key={suggestion.description}
																{...getSuggestionItemProps(
																	suggestion
																)}
																className={classNames(
																	{
																		"address-autocomplete__item--active":
																			suggestion.active,
																	},
																	"address-autocomplete__item"
																)}
															>
																<span>
																	{suggestion.description}
																</span>
															</div>
														);
													})}
													<div className="address-autocomplete__dropdown-footer">
														<img
															src="/static/icons/powered_by_google.png"
															alt="powered by google"
														/>
													</div>
												</div>
											)}
										</div>
									);
								}}
							</PlacesAutocomplete>
						) : (
							<div style={{ position: "relative" }}>
								<InputFormik
									id={id}
									name={name}
									isRequired={isRequired}
									disabled
									label={label}
									maxLength={50}
								/>
								<div className="address-autocomplete__loader">
									<HorizontalDotLoader dotSize="3px" color="#3493dc" />
								</div>
							</div>
						)}
					</div>
				);
			}}
		</Field>
	);
};

AddressAutocompleteFormik.defaultProps = {
	shop: "",
	isRequired: false,
	coords: {},
};

AddressAutocompleteFormik.propTypes = {
	id: PropTypes.string,
	shop: PropTypes.string,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	onPlaceSelect: PropTypes.func,
	onBeforePlaceSelect: PropTypes.func,
	onError: PropTypes.func,
	name: PropTypes.string,
	isRequired: PropTypes.bool,
	enableSuggestionWithPostCode: PropTypes.bool,
	setFieldValue: PropTypes.func.isRequired,
	setFieldError: PropTypes.func.isRequired,
	blocPrefixName: PropTypes.string,
};

export default AddressAutocompleteFormik;
